import RadioButtonField from "@features/onboarding/components/RadioButtonField";
import {
  BIO_PRESETS,
  BIO_QUESTIONS,
  BIO_TEXT_MAX,
  type BioContent,
  getBioPreset,
  getBioQuestion,
} from "@fitties/shared";
import clsx from "clsx";
import { useState } from "react";
import Container from "./Container";
import TextareaField from "./TextareaField";

export type BioPickerKind = BioContent["kind"];

export interface BioPickerProps {
  value: BioContent | null;
  onChange: (value: BioContent | null) => void;
  error?: string;
  charCountTestId?: string;
}

function initialCustomText(value: BioContent | null): string {
  if (!value) {
    return "";
  }
  if (value.kind === "custom") {
    return value.text;
  }
  return "";
}

function choiceClass(selected: boolean): string {
  return clsx(
    "rounded-lg border-2 px-3 py-2 text-left text-sm",
    selected ? "border-pink-600 bg-pink-50" : "border-gray-200 bg-white"
  );
}

function draftToContent(
  kind: BioPickerKind,
  questionId: string,
  answer: string,
  presetId: string,
  customText: string
): BioContent | null {
  if (kind === "qa") {
    const question = getBioQuestion(questionId);
    const trimmed = answer.trim();
    if (!question || !trimmed) {
      return null;
    }
    return { kind: "qa", questionId: question.id, question: question.question, answer: trimmed };
  }
  if (kind === "preset") {
    const preset = getBioPreset(presetId);
    if (!preset) {
      return null;
    }
    return { kind: "preset", presetId: preset.id, text: preset.text };
  }
  const trimmed = customText.trim();
  if (!trimmed) {
    return null;
  }
  return { kind: "custom", text: trimmed };
}

function KindOption({
  kind,
  label,
  testId,
  expanded,
  onSelect,
}: {
  kind: BioPickerKind;
  label: string;
  testId: string;
  expanded: BioPickerKind | null;
  onSelect: (kind: BioPickerKind) => void;
}) {
  return (
    <Container>
      <RadioButtonField
        name="bio-kind"
        value={kind}
        label={label}
        formValue={expanded ?? ""}
        testId={testId}
        onChange={() => onSelect(kind)}
      />
    </Container>
  );
}

export function BioPicker({ value, onChange, error, charCountTestId = "character-count" }: BioPickerProps) {
  const [expanded, setExpanded] = useState<BioPickerKind | null>(value?.kind ?? null);
  const [kind, setKind] = useState<BioPickerKind>(value?.kind ?? "custom");
  const [questionId, setQuestionId] = useState(value?.kind === "qa" ? value.questionId : "");
  const [answer, setAnswer] = useState(value?.kind === "qa" ? value.answer : "");
  const [presetId, setPresetId] = useState(value?.kind === "preset" ? value.presetId : "");
  const [customText, setCustomText] = useState(initialCustomText(value));

  const emit = (
    nextKind: BioPickerKind,
    nextQuestionId: string,
    nextAnswer: string,
    nextPresetId: string,
    nextCustom: string
  ) => {
    onChange(draftToContent(nextKind, nextQuestionId, nextAnswer, nextPresetId, nextCustom));
  };

  const openKind = (nextKind: BioPickerKind) => {
    setExpanded(nextKind);
    setKind(nextKind);
    emit(nextKind, questionId, answer, presetId, customText);
  };

  const activeLength = kind === "qa" ? answer.length : kind === "custom" ? customText.length : 0;

  return (
    <div className="space-y-3 text-left" data-testid="bio-picker" data-expanded={expanded ?? "none"}>
      <section className="space-y-2" data-testid="bio-section-qa">
        <KindOption
          kind="qa"
          label="Answer a question"
          testId="bio-option-qa"
          expanded={expanded}
          onSelect={openKind}
        />
        {expanded === "qa" ? (
          <>
            <div className="flex flex-col gap-2">
              {BIO_QUESTIONS.map((question) => {
                const selected = kind === "qa" && questionId === question.id;
                return (
                  <button
                    key={question.id}
                    type="button"
                    data-testid={`bio-question-${question.id}`}
                    onClick={() => {
                      setKind("qa");
                      setQuestionId(question.id);
                      emit("qa", question.id, answer, presetId, customText);
                    }}
                    className={choiceClass(selected)}
                  >
                    {question.question}
                  </button>
                );
              })}
            </div>
            {questionId ? (
              <div className="relative mt-6" data-testid="bio-answer-field">
                <Container>
                  <TextareaField
                    name="bio_answer"
                    aria-label="Bio answer"
                    value={answer}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                      const next = event.target.value;
                      setAnswer(next);
                      emit("qa", questionId, next, presetId, customText);
                    }}
                    placeholder="Write your answer"
                    error={error}
                    className="min-h-14 text-lg font-semibold"
                    maxLength={BIO_TEXT_MAX}
                  />
                </Container>
                <p className="mt-1 text-right text-sm text-gray-500" data-testid={charCountTestId} aria-live="polite">
                  {activeLength}/{BIO_TEXT_MAX}
                </p>
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      <section className="space-y-2" data-testid="bio-section-preset">
        <KindOption
          kind="preset"
          label="Use one of ours"
          testId="bio-option-preset"
          expanded={expanded}
          onSelect={openKind}
        />
        {expanded === "preset" ? (
          <div className="flex flex-col gap-2">
            {BIO_PRESETS.map((preset) => {
              const selected = kind === "preset" && presetId === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  data-testid={`bio-preset-${preset.id}`}
                  onClick={() => {
                    setKind("preset");
                    setPresetId(preset.id);
                    emit("preset", questionId, answer, preset.id, customText);
                  }}
                  className={choiceClass(selected)}
                >
                  {preset.text}
                </button>
              );
            })}
          </div>
        ) : null}
      </section>

      <section className="space-y-2" data-testid="bio-section-custom">
        <KindOption
          kind="custom"
          label="Write your own"
          testId="bio-write-own-open"
          expanded={expanded}
          onSelect={openKind}
        />
        {expanded === "custom" ? (
          <div className="relative">
            <Container>
              <TextareaField
                name="bio"
                aria-label="Bio"
                value={customText}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                  const next = event.target.value;
                  setKind("custom");
                  setCustomText(next);
                  emit("custom", questionId, answer, presetId, next);
                }}
                placeholder="A funny one-liner — don't overthink it"
                error={error}
                className="min-h-14 text-lg font-semibold"
                maxLength={BIO_TEXT_MAX}
              />
            </Container>
            <p className="mt-1 text-right text-sm text-gray-500" data-testid={charCountTestId} aria-live="polite">
              {activeLength}/{BIO_TEXT_MAX}
            </p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
